import Cookie from 'js-cookie';

export type TMobileEventBridge = Pick<
	RallySSO,
	| 'initialize'
	| 'getAccessToken'
	| 'getApp'
	| 'getUser'
	| 'showAlert'
	| 'close'
	| 'openLeaderboard'
	| 'inviteFriend'
	| 'inviteFriends'
	| 'inviteToGroup'
> & {
	launchExternal: (url: string) => void;
};

export default class RallySSO {
	protected static readonly NATIVE_BRIDGE_COOKIE_KEY = 'native_cookies';
	// eslint-disable-next-line sonarjs/public-static-readonly
	static nativeBridge?: TMobileEventBridge;

	/**
	 * Check if website has been loaded inside webview.
	 */
	public static BUILD_IS_NATIVE(): boolean {
		if( window.location.href.includes('loc') ) {
			return false;
		}
		const searchParams = new URLSearchParams(window.location.search);

		const isNative = Boolean(searchParams.get('isNative'));
		const isNativeCookie = JSON.parse(
			Cookie.get(this.NATIVE_BRIDGE_COOKIE_KEY) || 'false',
		) as boolean;

		if( isNative ) {
			Cookie.set(this.NATIVE_BRIDGE_COOKIE_KEY, 'true');
		}

		return isNativeCookie || isNative;
	}

	static readonly IS_NATIVE = RallySSO.BUILD_IS_NATIVE();

	public async init() {
		try {
			window.logger.push(`setNativeBridge`);
			await this.setNativeBridge();
		} catch ( error ) {
			if( typeof error === 'string' ) {
				window.logger.push(error);
				throw new Error(`Initialization Error: ${error}`);
			}

			throw new Error(`Initialization Error: ${( error as Error )?.message}`);
		}
	}

	public async initialize(callback: ( () => void ) | string): Promise<void> {
		return Promise.resolve(RallySSO.nativeBridge?.initialize(callback));
	}

	public async getAccessToken(): Promise<string | null> {
		if( RallySSO.IS_NATIVE ) {
			return RallySSO.nativeBridge?.getAccessToken() || null;
		}

		return Promise.resolve(null);
	}

	public async getApp(): Promise<string | null> {
		if( RallySSO.IS_NATIVE ) {
			return RallySSO.nativeBridge?.getApp() || null;
		}

		return Promise.resolve(null);
	}

	public getUser(): null {
		window.logger.push(`getUser: IS_NATIVE : ${String(RallySSO.IS_NATIVE)}`);
		if( RallySSO.IS_NATIVE ) {
			return RallySSO.nativeBridge?.getUser() || null;
		}

		return null;
	}

	public openLeaderboard(): void {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.openLeaderboard();
		}
	}

	public showAlert(message: string) {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.showAlert(message);
		}
	}

	public static launchExternal(url: string) {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.launchExternal(url);
		} else {
			window.location.href = url;
		}
	}


	public inviteFriend() {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.inviteFriend();
		}
	}

	public inviteFriends() {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.inviteFriends();
		}
	}

	public close() {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.close();
		}
	}

	public inviteToGroup(gameId: string, groupId: string) {
		if( RallySSO.IS_NATIVE ) {
			RallySSO.nativeBridge?.inviteToGroup(gameId, groupId);
		}
	}

	/**
	 * Initialize sso for apps
	 * the API is stored in mlbNative global variable. Do not change it to window.pgNative
	 * timeout has been added by MLB request - 20 seconds
	 */
	private setNativeBridge() {
		return new Promise((resolve, reject) => {
			let timer: NodeJS.Timeout;
			let timesCalled = 0;
			const checkState = (): void => {
				if( typeof mlbNative !== 'undefined' && !RallySSO.nativeBridge ) {
					RallySSO.nativeBridge = mlbNative;

					window.logger.push(`nativeBridge ready`);

					clearTimeout(timer);
					resolve(RallySSO.nativeBridge);
				} else {
					timesCalled = timesCalled + 1;
					const TIMES_LIMIT = 50;
					if( timesCalled === TIMES_LIMIT ) {
						clearTimeout(timer);
						window.logger.push(`nativeBridge error`);
						reject(new Error('Native bridge was not set'));
						return;
					}
					const TIMEOUT = 200;
					timer = setTimeout(checkState, TIMEOUT);
				}
			};

			checkState();
		});
	}
}
